import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {SizeSelection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {useTranslation} from '@aboutyou/i18n';
import {useIsFitAnalyticsConsetGiven} from '@aboutyou/storage';
import {useShowToast} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {SizeAdvisorWidget} from '../../../../features/SizeAdvisor/SizeAdvisorWidget.lazy';
import {useSizeAdvisorOpenedState} from '../../utils';
import {useTrackMobileAdpFeature} from '../../utils/useTrackMobileAdpFeature';
import {SizeAdvisorButton} from './SizeAdvisorButton';

interface SizeAdvisorSectionProps {
  // Since we can reuse the sizeAdvisor in multiple places
  // the id is used to create an unique opener key for the opened state manager
  id?: string;
  product: Product | undefined;
  onSizeSelect: (sizeId: number) => void;
  sizeSelection: SizeSelection | undefined;
  className?: string;
  shouldUseMySizeFinderButton?: boolean;
  style?: React.CSSProperties;
}

export const SizeAdvisorSection: React.FC<SizeAdvisorSectionProps> = ({
  id,
  product,
  sizeSelection,
  onSizeSelect,
  className,
  shouldUseMySizeFinderButton,
  style,
}) => {
  const {t} = useTranslation();
  const {isOpen, open, cancel, hide} = useSizeAdvisorOpenedState(id);
  const {trackMobileAdpFeature} = useTrackMobileAdpFeature();
  const isConsentBannerAccepted = useIsFitAnalyticsConsetGiven();
  const showToast = useShowToast();

  const handleSizeAdvisorClick = () => {
    open();
    trackMobileAdpFeature?.({eventType: 'click', label: 'adp|sizeFinder'});
  };

  const onError = (
    errorTitle: unknown | null,
    errorMessage: unknown | null,
  ) => {
    const errorNotificationTitle =
      typeof errorTitle === 'string'
        ? errorTitle
        : t(
            'Sorry, unfortunately we do not have a size recommendation for this product',
          );

    showToast({
      content: (
        <Container>
          <span>{errorNotificationTitle}</span>
          {typeof errorMessage === 'string' && <span>{errorMessage}</span>}
        </Container>
      ),
      type: 'ERROR',
      'data-testid': 'statusPopupError',
    });
  };

  if (!product || !sizeSelection || !isConsentBannerAccepted) {
    return null;
  }

  return (
    <div className={className} style={style}>
      <SizeAdvisorButton
        sizeSelection={sizeSelection}
        productId={product.id}
        onClick={handleSizeAdvisorClick}
        shouldUseMySizeFinderButton={shouldUseMySizeFinderButton}
      />
      {isOpen && (
        <SizeAdvisorWidget
          product={product}
          isOpen={isOpen}
          closeSizeAdvisorOnCancel={cancel}
          closeSizeAdvisorOnHistoryBack={hide}
          onSizeSelect={onSizeSelect}
          shopSizeRunSelected={false}
          onError={onError}
        />
      )}
    </div>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;
